<template>
  <div class="content pkg-page">
    <!-- ============================================================
         TOOLBAR CARD: Bộ lọc + Tác vụ nhanh
         ============================================================ -->
    <div class="pkg-toolbar-card row-data">
      <!-- Vùng Bộ lọc -->
      <div class="pkg-toolbar-section pkg-toolbar-section--filter">
        <div class="pkg-card-head">
          <div>
            <p class="pkg-card-title">Bộ lọc tìm kiếm</p>
            <p class="pkg-card-subtitle">Lọc theo tên, đơn vị tính và trạng thái gia hạn.</p>
          </div>
        </div>

        <div class="pkg-filter-grid">
          <!-- Tìm theo tên -->
          <el-input
            v-model="searchRequest.keyword"
            placeholder="Nhập tên gói học phí..."
            clearable
            class="pkg-search-input"
            @keyup.enter.native="handleSearch"
            @clear="handleSearch"
          >
            <template #append>
              <el-button icon="el-icon-search" @click="handleSearch" />
            </template>
          </el-input>

          <el-select
            v-model="searchRequest.billingCycle"
            placeholder="Đơn vị tính"
            clearable
            class="pkg-filter-select"
            @change="handleSearch"
          >
            <el-option label="Hàng ngày" value="DAILY" />
            <el-option label="Hàng tháng" value="MONTHLY" />
            <el-option label="Hàng năm" value="YEARLY" />
            <el-option label="Một lần" value="ONE_TIME" />
          </el-select>

          <el-select
            v-model="searchRequest.autoRenew"
            placeholder="Trạng thái gia hạn"
            clearable
            class="pkg-filter-select"
            @change="handleSearch"
          >
            <el-option label="Đang bật" :value="true" />
            <el-option label="Đang tắt" :value="false" />
          </el-select>
        </div>
      </div>

      <!-- Đường kẻ ngăn cách -->
      <div class="pkg-toolbar-divider" />

      <!-- Vùng Tác vụ nhanh -->
      <div class="pkg-toolbar-section pkg-toolbar-section--action">
        <div class="pkg-card-head pkg-card-head--compact">
          <div>
            <p class="pkg-card-title">Tác vụ nhanh</p>
            <p class="pkg-card-subtitle">
              Thêm mới gói học phí vào hệ thống.
            </p>
          </div>
        </div>
        <div class="pkg-action-group">
          <el-button
            type="primary"
            plain
            class="pkg-action-button"
            @click="openGuideDialog"
          >
            <i class="el-icon-question" />
            Hướng dẫn
          </el-button>
          <el-button
            type="success"
            class="pkg-action-button pkg-action-button--primary"
            @click="openCreateDialog"
          >
            <i class="el-icon-plus" />
            Thêm mới
          </el-button>
        </div>
      </div>
    </div>

    <!-- ============================================================
         TABLE CARD: Danh sách gói học phí
         ============================================================ -->
    <div class="pkg-table-card row-data">
      <!-- Header bảng -->
      <div class="pkg-card-head pkg-table-head">
        <div>
          <p class="pkg-card-title">Danh sách gói học phí</p>
          <p class="pkg-card-subtitle">
            Quản lý toàn bộ gói học phí áp dụng trong trường.
          </p>
        </div>
      </div>

      <!-- Bảng dữ liệu -->
      <div class="pkg-table-wrapper">
        <el-table
          v-loading="tableLoading"
          :data="packageList"
          border
          class="sk-table"
          highlight-current-row
          :max-height="$tableMaxHeightSmall"
        >
          <!-- STT -->
          <el-table-column
            label="STT"
            type="index"
            width="55"
            align="center"
            :index="indexMethod"
          />

          <!-- Tên gói -->
          <el-table-column label="Tên gói học phí" prop="name" min-width="220" />

          <!-- Mô tả -->
          <el-table-column label="Mô tả" min-width="300">
            <template slot-scope="{ row }">
              <div>
                <div :class="['pkg-desc', { expanded: isDescriptionExpanded(row.id) }]">
                  {{ row.description || '—' }}
                </div>
                <div v-if="showDescriptionToggle(row.description)" class="pkg-desc-toggle">
                  <a href="#" @click.prevent="toggleDescription(row.id)">
                    {{ isDescriptionExpanded(row.id) ? 'Thu gọn' : 'Xem thêm' }}
                  </a>
                </div>
              </div>
            </template>
          </el-table-column>

          <!-- Chu kỳ tính phí -->
          <el-table-column label="Chu kỳ tính phí" min-width="180" align="center">
            <template slot-scope="{ row }">
              {{ formatBillingCycle(row.billingCycle, row.cycleDuration) }}
            </template>
          </el-table-column>

          <!-- Tự động gia hạn -->
          <el-table-column label="Tự động gia hạn" width="135" align="center">
            <template slot-scope="{ row }">
              <el-tag :type="row.autoRenew ? 'success' : 'info'" size="small">
                {{ row.autoRenew ? 'Đang bật' : 'Đang tắt' }}
              </el-tag>
            </template>
          </el-table-column>

          <el-table-column label="SL thành viên" width="120" align="center">
            <template slot-scope="{ row }">
              <span>{{row.memberCount}} / {{ row.maxMembers || '∞' }}</span>
            </template>
          </el-table-column>

          <!-- Trạng thái -->
          <el-table-column label="Trạng thái" width="120" align="center">
            <template slot-scope="{ row }">
              <el-tag :type="getStatusType(row.status)" size="small">
                {{ getStatusLabel(row.status) }}
              </el-tag>
            </template>
          </el-table-column>

          <!-- Ngày tạo -->
          <el-table-column label="Ngày tạo" width="155" align="center">
            <template slot-scope="{ row }">
              <span>{{ formatDateTime(row.createdDate) }}</span>
            </template>
          </el-table-column>

          <!-- Tác vụ -->
          <el-table-column label="Tác vụ" width="200" align="center" fixed="right">
            <template slot-scope="{ row }">
              <el-tooltip content="Quản lý gia hạn" placement="top">
                <el-button
                  type="warning"
                  icon="el-icon-refresh"
                  size="mini"
                  circle
                  plain
                  @click="openRenewalDialog(row)"
                />
              </el-tooltip>
              <el-tooltip content="Quản lý thành viên" placement="top">
                <el-button
                  type="success"
                  icon="el-icon-user-solid"
                  size="mini"
                  circle
                  plain
                  class="ml-1"
                  @click="openMemberDialog(row)"
                />
              </el-tooltip>
              <el-tooltip content="Chỉnh sửa" placement="top">
                <el-button
                  type="primary"
                  icon="el-icon-edit"
                  size="mini"
                  circle
                  plain
                  class="ml-1"
                  @click="openEditDialog(row)"
                />
              </el-tooltip>
              <el-tooltip content="Xóa gói học phí" placement="top">
                <el-button
                  type="danger"
                  icon="el-icon-delete"
                  size="mini"
                  circle
                  plain
                  class="ml-1"
                  @click="handleDelete(row)"
                />
              </el-tooltip>
            </template>
          </el-table-column>
        </el-table>
      </div>

      <!-- Phân trang -->
      <div class="pkg-pagination">
        <el-pagination
          background
          :current-page.sync="pagination.page"
          :page-sizes="[25, 50, 100, 200]"
          :page-size.sync="pagination.size"
          :total="pagination.total"
          layout="sizes, prev, pager, next"
          @size-change="handleSizeChange"
          @current-change="handlePageChange"
        />
      </div>
    </div>

    <!-- ============================================================
         DIALOG THÊM MỚI / CẬP NHẬT / GIA HẠN / HƯỚNG DẪN
         ============================================================ -->
    <package-form-dialog ref="pkgFormDialog" @closed="handleRefreshAll" />
    
    <package-member-dialog ref="memberDialog" @closed="handleRefreshAll" />

    <package-renewal-dialog ref="renewalDialog" @closed="handleRefreshAll" />

    <package-guide-dialog ref="guideDialog" />
  </div>
</template>

<script>
import PackageManagementService from "@/services/tutition_v3/PackageManagementService";
import PackageFormDialog from "@/components/tutition_v3/dialog/PackageFormDialog.vue";
import PackageMemberDialog from "@/components/tutition_v3/dialog/PackageMemberDialog.vue";
import PackageRenewalDialog from "@/components/tutition_v3/dialog/PackageRenewalDialog.vue";
import PackageGuideDialog from "@/components/tutition_v3/dialog/PackageGuideDialog.vue";
import NotifyMessage from "@/commonFuncion/NotifyMessage";

export default {
  name: "ManagingPackage",
  components: {
    PackageFormDialog,
    PackageMemberDialog,
    PackageRenewalDialog,
    PackageGuideDialog,
  },

  data() {
    return {
      tableLoading: false,
      packageList: [],

      searchRequest: {
        keyword: "",
        billingCycle: null,
        autoRenew: null,
      },

      // page FE bắt đầu từ 1, BE từ 0
      pagination: { page: 1, size: 25, total: 0 },
      expandedDescriptionIds: [],
    };
  },

  created() {
    this.fetchData();
  },

  methods: {
    // ─── Helpers ──────────────────────────────────────────────────
    indexMethod(index) {
      return (this.pagination.page - 1) * this.pagination.size + index + 1;
    },

    formatDateTime(value) {
      if (!value) return "—";
      try {
        const d = new Date(value);
        const pad = (n) => String(n).padStart(2, "0");
        return `${pad(d.getDate())}/${pad(d.getMonth() + 1)}/${d.getFullYear()} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
      } catch {
        return value;
      }
    },

    isDescriptionExpanded(id) {
      return this.expandedDescriptionIds.includes(id);
    },
    toggleDescription(id) {
      const idx = this.expandedDescriptionIds.indexOf(id);
      if (idx === -1) this.expandedDescriptionIds.push(id);
      else this.expandedDescriptionIds.splice(idx, 1);
    },
    showDescriptionToggle(desc) {
      return desc && String(desc).length > 80;
    },

    // ─── Formatting Helpers ────────────────────────────────────────

    formatBillingCycle(cycle, duration) {
      const map = {
        DAILY: "Hàng ngày",
        MONTHLY: "Hàng tháng",
        YEARLY: "Hàng năm",
        ONE_TIME: "Một lần"
      };
      const label = map[cycle] || "—";
      if (['MONTHLY', 'YEARLY'].includes(cycle) && duration > 1) {
        return `${duration} ${label.replace('Hàng ', '').replace('năm', 'năm')}`; // rough translation e.g. 3 tháng
      }
      return label;
    },
    getStatusLabel(status) {
      const map = {
        PENDING: "Chưa bắt đầu",
        ACTIVE: "Đang hiệu lực",
        EXPIRED: "Đã hết hạn"
      };
      return map[status] || status || "—";
    },
    getStatusType(status) {
      const map = {
        PENDING: "warning",
        ACTIVE: "success",
        EXPIRED: "info"
      };
      return map[status] || "info";
    },

    // ─── API ──────────────────────────────────────────────────────

    /**
     * Tải danh sách gói học phí.
     * page FE (1-based) → trừ 1 trước khi gửi lên BE (0-based).
     */
    async fetchData() {
      this.tableLoading = true;
      try {
        const params = {
          ...this.searchRequest,
          page: this.pagination.page - 1,
          size: this.pagination.size,
        };
        // Loại bỏ key rỗng / null / undefined
        Object.keys(params).forEach((k) => {
          if (params[k] === "" || params[k] === null || params[k] === undefined) {
            delete params[k];
          }
        });

        const res = await PackageManagementService.search(params);
        this.packageList = res.data.data || []
        this.pagination.total = res.data.total || 0;
      } catch (err) {
        NotifyMessage.leftCornerNotificationError(
          err?.response?.data?.message || "Không thể tải danh sách gói học phí!"
        );
      } finally {
        this.tableLoading = false;
      }
    },

    handleSearch() {
      this.pagination.page = 1;
      this.fetchData();
    },

    handleRefreshAll() {
      this.searchRequest.keyword = "";
      this.searchRequest.billingCycle = null;
      this.searchRequest.autoRenew = null;
      this.pagination.page = 1;
      this.fetchData();
    },

    handleSizeChange(size) {
      this.pagination.size = size;
      this.pagination.page = 1;
      this.fetchData();
    },

    handlePageChange(page) {
      this.pagination.page = page;
      this.fetchData();
    },

    /**
     * Xóa mềm – hiển thị $confirm trước khi gọi API DELETE.
     */
    async handleDelete(row) {
      try {
        await this.$confirm(
          "Bạn có chắc chắn muốn xóa gói học phí này? Thao tác không thể hoàn tác.",
          "Xác nhận xóa",
          { confirmButtonText: "Xóa", cancelButtonText: "Hủy", type: "warning" }
        );
      } catch {
        return;
      }

      try {
        const res = await PackageManagementService.remove(row.id);
        NotifyMessage.leftCornerNotificationSuccess(
          res.data.message || "Xóa gói học phí thành công!"
        );
        this.handleRefreshAll();
      } catch (err) {
        NotifyMessage.leftCornerNotificationError(
          err?.response?.data?.message || "Xóa gói học phí thất bại!"
        );
      }
    },

    // ─── Mở dialog ────────────────────────────────────────────────
    openCreateDialog() {
      this.$refs.pkgFormDialog.openForCreate();
    },

    openEditDialog(row) {
      this.$refs.pkgFormDialog.openForEdit(row.id);
    },

    openMemberDialog(row) {
      this.$refs.memberDialog.open(row);
    },

    openRenewalDialog(row) {
      this.$refs.renewalDialog.open(row);
    },

    openGuideDialog() {
      this.$refs.guideDialog && this.$refs.guideDialog.open();
    },
  },
};
</script>

<style lang="scss" scoped>
@import "@/assets/style/sk-table.scss";

/* ===================================================
   Trang quản lý gói học phí – PKG Page Layout
   =================================================== */

.pkg-page {
  padding: 4px 0 16px;

  .row-data {
    margin-bottom: 20px;
  }
}

/* ─── Toolbar card ─────────────────────────────────── */
.pkg-toolbar-card {
  display: flex;
  align-items: flex-start;
  gap: 18px;
  padding: 18px;
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 18px;
  box-shadow: 0 10px 24px rgba(15, 23, 42, 0.05);
}

.pkg-toolbar-section { min-width: 0; }
.pkg-toolbar-section--filter  { flex: 2; }
.pkg-toolbar-section--action  { flex: 0 0 auto; display: flex; flex-direction: column; justify-content: space-between; }

.pkg-toolbar-divider {
  width: 1px;
  align-self: stretch;
  background: #e2e8f0;
  flex-shrink: 0;
  margin: 0 4px;
}

/* ─── Table card ───────────────────────────────────── */
.pkg-table-card {
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 18px;
  box-shadow: 0 10px 24px rgba(15, 23, 42, 0.05);
  padding: 18px 18px 14px;
}

/* ─── Card head ────────────────────────────────────── */
.pkg-card-head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 16px;
}
.pkg-card-head--compact { margin-bottom: 14px; }
.pkg-table-head { align-items: center; }

.pkg-card-title {
  margin: 0 0 4px;
  font-size: 15px;
  font-weight: 700;
  color: #1e293b;
  word-break: break-word;
  overflow-wrap: break-word;
  white-space: normal;
}
.pkg-card-subtitle {
  margin: 0;
  font-size: 12.5px;
  line-height: 1.5;
  color: #64748b;
  word-break: break-word;
  overflow-wrap: break-word;
  white-space: normal;
}

/* ─── Bộ lọc ──────────────────────────────────────── */
.pkg-filter-grid {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  align-items: center;
}
.pkg-search-input { width: 320px; }
.pkg-filter-select { width: 170px; }

/* ─── Tác vụ nhanh ────────────────────────────────── */
.pkg-action-group {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
}
.pkg-action-button { min-width: 130px; font-weight: 600; }
.pkg-action-button--primary { box-shadow: 0 6px 16px rgba(34, 197, 94, 0.2); }

/* ─── Bảng ────────────────────────────────────────── */
.pkg-table-wrapper {
  border-radius: 14px;
  overflow: hidden;
}

.pkg-text-muted { color: #94a3b8; font-size: 13px; word-break: break-word; white-space: normal; }

/* Tiêu đề cột bảng: nền xanh đậm */
/deep/ .sk-table thead th,
/deep/ .sk-table thead td {
  background: #2563eb !important;
  color: #ffffff !important;
  font-weight: 700;
  border-bottom: 1px solid rgba(255,255,255,0.08);
}
/deep/ .sk-table thead th > .cell,
/deep/ .sk-table thead td > .cell {
  color: #ffffff !important;
  font-weight: 700;
  white-space: normal !important;
  word-break: break-word !important;
  overflow-wrap: break-word !important;
  line-height: 1.3 !important;
}
/deep/ .sk-table thead tr { height: 56px; }
/deep/ .sk-table tbody td { background: #ffffff; }
/deep/ .sk-table tbody td > .cell {
  white-space: normal !important;
  word-break: break-word !important;
  overflow-wrap: break-word !important;
  line-height: 1.4 !important;
}
/deep/ .sk-table .el-tag {
  white-space: normal !important;
  height: auto !important;
  line-height: 1.3 !important;
  padding: 4px 8px !important;
  word-break: break-word !important;
  display: inline-block;
}

/* ─── Phân trang ──────────────────────────────────── */
.pkg-pagination {
  display: flex;
  justify-content: center;
  margin-top: 16px;
}

/* ─── Input / Button bo góc ─────────────────────────── */
/deep/ .pkg-page .el-input__inner,
/deep/ .pkg-page .el-select .el-input__inner {
  border-radius: 10px;
}
/deep/ .pkg-page .el-button { border-radius: 10px; }
/deep/ .pkg-page .el-input-group__append .el-button {
  border-top-left-radius: 0;
  border-bottom-left-radius: 0;
}

/* ─── Responsive ──────────────────────────────────── */
@media (max-width: 1200px) {
  .pkg-toolbar-card {
    flex-direction: column;
    align-items: stretch;
  }
  .pkg-toolbar-divider {
    width: 100%;
    height: 1px;
    margin: 4px 0;
  }
  .pkg-table-head {
    flex-direction: column;
    align-items: flex-start;
    gap: 8px;
  }
}

@media (max-width: 768px) {
  .pkg-filter-grid,
  .pkg-action-group {
    flex-direction: column;
    align-items: stretch;
  }
  .pkg-search-input,
  .pkg-action-button {
    width: 100%;
  }
}

/* Mô tả: clamp 2 dòng, show toggle */
.pkg-desc {
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: normal;
  word-break: break-word;
  overflow-wrap: break-word;
}
.pkg-desc.expanded {
  -webkit-line-clamp: unset;
  display: block;
  overflow: visible;
  word-break: break-word;
  overflow-wrap: break-word;
  white-space: normal;
}
.pkg-desc-toggle { margin-top: 6px; }
.pkg-desc-toggle a { color: #2563eb; cursor: pointer; font-size: 13px; }
</style>
